import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  hospitalityHousekeepingTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, HousekeepingTaskDetail } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-hospitality-housekeeping',
  imports: [DatePipe, AinPageComponent, AinStateComponent, TableModule, ButtonModule, FormsModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.hospitality.housekeeping.title')" [description]="t('catalog.hospitality.housekeeping.description')">
      @if (loading() && rows().length === 0) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <p-select
              [options]="statusOptions"
              [(ngModel)]="statusFilter"
              (ngModelChange)="load()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.tracking.status')"
              appendTo="body" />
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>
          <p-table [value]="rows()" [paginator]="true" [rows]="20">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.hospitality.roomNumber') }}</th>
                <th>{{ t('catalog.hospitality.floor') }}</th>
                <th>{{ t('catalog.hospitality.taskType') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.hospitality.assignedTo') }}</th>
                <th>{{ t('catalog.hospitality.dueAt') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.roomNumber || row.roomUnitId }}</td>
                <td>{{ row.floor || '—' }}</td>
                <td>{{ row.taskType }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.assignedToReference || '—' }}</td>
                <td>{{ row.dueAtUtc | date:'short' }}</td>
                <td class="actions">
                  @if (row.status === 'Pending' || row.status === 'InProgress') {
                    <input pInputText [(ngModel)]="assignRefs[row.id]" [placeholder]="t('catalog.hospitality.assignedTo')" />
                    <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.hospitality.action.assignTask')" (click)="assign(row.id)"></button>
                    <button pButton type="button" size="small" [label]="t('catalog.hospitality.action.completeTask')" (click)="complete(row.id)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
  `]
})
export class HospitalityHousekeepingComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  readonly loading = signal(true);
  readonly rows = signal<HousekeepingTaskDetail[]>([]);
  statusFilter = '';
  assignRefs: Record<string, string> = {};

  readonly statusOptions = [
    { label: 'All', value: '' },
    { label: 'Pending', value: 'Pending' },
    { label: 'InProgress', value: 'InProgress' },
    { label: 'Completed', value: 'Completed' }
  ];

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.hospitalityHousekeepingChanged))
      .subscribe(() => this.load(false));

    void this.setupLiveSubscription();
    this.load();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.listHousekeepingTasks(this.statusFilter || undefined).subscribe({
      next: rows => { this.rows.set(rows); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }

  assign(id: string): void {
    const ref = this.assignRefs[id]?.trim();
    if (!ref) return;
    this.service.assignHousekeepingTask(id, ref).subscribe({
      next: () => this.load(false)
    });
  }

  complete(id: string): void {
    this.service.completeHousekeepingTask(id).subscribe({
      next: () => this.load(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;

    this.subscribedTopic = hospitalityHousekeepingTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }
}
